import { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import PropTypes from "prop-types";

import ChartBlock, {
  chartHasSeriesData,
  pricingLineChartOptions,
} from "../../../routes/pricing-reports/ChartBlock";
import DesignAccordion from "../../../routes/pricing-reports/design/Accordion";
import { TOP_PRODUCT_MIN_COUNT, TOP_PRODUCT_SLICE_LIMIT } from "./productFrequencyUtils";

const frequencyChartFrameSx = { p: 3.5 };

export const frequencyPieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: { top: 16, right: 28, bottom: 16, left: 20 },
  },
  plugins: {
    legend: {
      display: true,
      position: "right",
      labels: {
        boxWidth: 12,
        padding: 12,
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = Number(context.parsed) || 0;
          const total = (context.dataset?.data || []).reduce(
            (sum, point) => sum + (Number(point) || 0),
            0
          );
          const percent = total ? ((value / total) * 100).toFixed(1) : "0.0";
          return `${context.label}: ${value.toLocaleString()} (${percent}%)`;
        },
      },
    },
  },
};

export const monthlyFrequencyBarChartOptions = {
  ...pricingLineChartOptions,
  layout: {
    padding: { top: 8, right: 12, bottom: 4, left: 4 },
  },
  plugins: {
    ...pricingLineChartOptions.plugins,
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = typeof context.parsed?.y === "number" ? context.parsed.y : 0;
          return `${context.dataset.label}: ${value.toLocaleString()}`;
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: { display: true, text: "Price observations" },
    },
  },
};

export const retailerSpreadBarChartOptions = {
  ...pricingLineChartOptions,
  indexAxis: "y",
  layout: {
    padding: { top: 8, right: 16, bottom: 8, left: 4 },
  },
  plugins: {
    ...pricingLineChartOptions.plugins,
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = typeof context.parsed?.x === "number" ? context.parsed.x : 0;
          return `${context.dataset.label}: ${value.toLocaleString()}`;
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      title: { display: true, text: "Price observations" },
    },
    y: {
      ticks: { autoSkip: false },
    },
  },
};

function ProductFrequencyChartsContent({
  topProductChart,
  underUtilizedChart,
  retailerSpreadChart,
  monthlyChart,
  monthlyProductName,
  isMonthlyLoading,
  monthlyError,
  isLoading,
  error,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <ChartBlock
          title="Top products"
          subtitle={`Top ${TOP_PRODUCT_SLICE_LIMIT} products with at least ${TOP_PRODUCT_MIN_COUNT} price observations.`}
          chartType="pie"
          chart={topProductChart}
          chartOptions={frequencyPieChartOptions}
          hasData={chartHasSeriesData(topProductChart)}
          isLoading={isLoading}
          error={error}
          emptyMessage={`No products have ${TOP_PRODUCT_MIN_COUNT} or more observations in this date range.`}
          sx={frequencyChartFrameSx}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <ChartBlock
          title="Under-utilized beef cuts"
          subtitle="Outside Skirt, Clod / Petite Tender, Inside Skirt, Bottom Sirloin Tri-Tip, Chuck Eye Roll, and Flank Steak."
          chartType="pie"
          chart={underUtilizedChart}
          chartOptions={frequencyPieChartOptions}
          hasData={chartHasSeriesData(underUtilizedChart)}
          isLoading={isLoading}
          error={error}
          emptyMessage="None of the under-utilized cuts have observations in this date range."
          sx={frequencyChartFrameSx}
        />
      </Box>
      <Box sx={{ minWidth: 0, gridColumn: "1 / -1" }}>
        <ChartBlock
          title="Retailer spread"
          subtitle="How price observations are distributed across retailers for the current date range."
          chartType="bar"
          chart={retailerSpreadChart}
          chartOptions={retailerSpreadBarChartOptions}
          hasData={chartHasSeriesData(retailerSpreadChart)}
          isLoading={isLoading}
          error={error}
          emptyMessage="No retailer counts for the current date range."
          sx={frequencyChartFrameSx}
        />
      </Box>
      {monthlyProductName ? (
        <Box sx={{ minWidth: 0, gridColumn: "1 / -1" }}>
          <ChartBlock
            title={`Monthly frequency — ${monthlyProductName}`}
            subtitle="Price observations grouped by month for the selected product."
            chartType="bar"
            chart={monthlyChart}
            chartOptions={monthlyFrequencyBarChartOptions}
            hasData={chartHasSeriesData(monthlyChart)}
            isLoading={isMonthlyLoading}
            error={monthlyError}
            emptyMessage="No monthly counts for this product in the current date range."
            sx={frequencyChartFrameSx}
          />
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Product / Frequency charts, wrapped in a collapsed accordion like Pricing Report.
 * Opening a product from the table expands the accordion so the monthly chart is visible.
 */
export default function ProductFrequencyCharts({
  topProductChart,
  underUtilizedChart,
  retailerSpreadChart,
  monthlyChart = null,
  monthlyProductName = "",
  isMonthlyLoading = false,
  monthlyError = null,
  isLoading = false,
  error = null,
}) {
  const [chartsExpanded, setChartsExpanded] = useState(false);

  useEffect(() => {
    if (monthlyProductName) {
      setChartsExpanded(true);
    }
  }, [monthlyProductName]);

  return (
    <DesignAccordion
      title="Frequency charts"
      subtitle="- Product counts, under-utilized cuts, and retailer spread."
      expanded={chartsExpanded}
      onChange={setChartsExpanded}
    >
      <Stack spacing={2} sx={{ width: "100%" }}>
        <ProductFrequencyChartsContent
          topProductChart={topProductChart}
          underUtilizedChart={underUtilizedChart}
          retailerSpreadChart={retailerSpreadChart}
          monthlyChart={monthlyChart}
          monthlyProductName={monthlyProductName}
          isMonthlyLoading={isMonthlyLoading}
          monthlyError={monthlyError}
          isLoading={isLoading}
          error={error}
        />
      </Stack>
    </DesignAccordion>
  );
}

const chartPropType = PropTypes.shape({
  labels: PropTypes.array,
  datasets: PropTypes.array,
});

ProductFrequencyChartsContent.propTypes = {
  topProductChart: chartPropType,
  underUtilizedChart: chartPropType,
  retailerSpreadChart: chartPropType,
  monthlyChart: chartPropType,
  monthlyProductName: PropTypes.string,
  isMonthlyLoading: PropTypes.bool,
  monthlyError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

ProductFrequencyCharts.propTypes = {
  topProductChart: chartPropType,
  underUtilizedChart: chartPropType,
  retailerSpreadChart: chartPropType,
  monthlyChart: chartPropType,
  monthlyProductName: PropTypes.string,
  isMonthlyLoading: PropTypes.bool,
  monthlyError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
